import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import {loadFocusedItem} from "redux/slices/dynamicMultipleElementsSlice";
import FocusedLoader, {FocusedError, FocusedNoData} from "./FocusedLoader";
import {capitalizeFirstLetter} from "helpers/stringHelper";
import ViewAllTitle from "components/ViewAllTitle";
import {FOCUS_DISPLAY_TYPE, FOCUS_ITEM_STYLE} from "../../../focusItems/enums";
import {MemoizedFocusedItemsProductView} from "./FocusedItemsProductView";
import CustomCarousel from "../carousel/CustomCarousel";
import {FocusedHorizontalList} from "./FocusedHorizontalList";
import DraggableFocusItemCard from 'pages/focusItems/components/DraggableFocusItemCard';

const FocusedItems = ({hasViewAllBtn, section, mobile = true}) => {
  const {[section.value]: focused} =
    useSelector((state) => state.dynamicMultipleElements.focused);

  return (
    <MemoizedFocusedItemsContents hasViewAllBtn={hasViewAllBtn} section={section} focused={focused} mobile={mobile}/>
  );
};

export default React.memo(FocusedItems);


const FocusedItemsContents = ({
                                section,
                                focused,
                                mobile,
                                sideClass = "app-side-pd",
                                showTitle = true
                              }) => {

  const {loading, data, init, error} = focused ?? {};

  const dispatch = useDispatch();

  const fetchFocusedItems = useCallback(() => {
    if (section?.value)
      dispatch(loadFocusedItem(section.value));
  }, [section, dispatch]);

  useEffect(() => {
    if (!init && !loading && !error)
      fetchFocusedItems();
  }, [fetchFocusedItems, init, loading, error]);


  return (

    <FocusedItemsContentDesign section={section} mobile={mobile} sideClass={sideClass} showTitle={showTitle}
                               focused={focused} fetchFocusedItems={fetchFocusedItems}/>
  );
};

const FocusItemCard = ({
  i,
  mobile,
  isCustomType,
  aspectRatio,
  isGridType,
  extras,
}) => {
  return (
    <div
      className={`${mobile ? 'app-rounded' : 'app-rounded'} overflow-hidden`}
      key={i.id}
    >
      {isCustomType ? (
        <div style={{ aspectRatio: `${aspectRatio}` }}>
          <img
            className="object-cover w-full h-full app-rounded"
            alt={i.title}
            src={mobile ? i.image_url : i.image_url_web}
          />
        </div>
      ) : (
        <div
          className={`${isGridType ? 'aspect-ratio-vertical-banner-image' : 'image-aspect-ratio force'}`}
        >
          <img
            className="object-cover w-full h-full app-rounded"
            alt={i.title}
            src={mobile ? i.image_url : i.image_url_web}
          />
        </div>
      )}

      {(i.sub_title || i.title) &&
        (!isCustomType || extras.use_image_titles) && (
          <div className="p-2 text-center bg-white">
            {i.title && (
              <div className="font-bold leading-snug truncate text-secondaryApp">
                {capitalizeFirstLetter(i.title)}
              </div>
            )}
            {i.sub_title && (
              <div className="text-xs leading-none truncate text-rand-100">
                {capitalizeFirstLetter(i.sub_title)}
              </div>
            )}
          </div>
        )}
    </div>
  )
}


export const FocusedItemsContentDesign = ({
                                     section,
                                     focused: {loading, data, init, error} = {},
                                     mobile,
                                     sideClass = "app-side-pd",
                                     showTitle = true,
                                     fetchFocusedItems,
                                     onUpdateFocusItem,
                                     allowDrag = false,
                                   }) => {
                              
                                    
  const { processing, updateSuccess, error:  errorMsg } = useSelector((state) => state.focusItem)
  const [modifiedData, setModifiedData] = useState({})

  const itemId = data?.id

  useEffect(() => {
    if (allowDrag) {
      setModifiedData(data)
    }
  }, [itemId, allowDrag])

  useEffect(() => {
    if(!processing && !updateSuccess && !!errorMsg) {
      setModifiedData(data)
    }
  }, [updateSuccess, processing])

  const finalData = allowDrag ? modifiedData : data
  const extras = section?.extras ?? {};

  //todo remove section?.display_type once its stable every where
  const displayType = extras.display_type ?? section?.display_type;
  const isGridType = displayType === FOCUS_DISPLAY_TYPE[0].value;
  const isProductViewType = displayType === FOCUS_DISPLAY_TYPE[2].value;
  const isListType = displayType === FOCUS_DISPLAY_TYPE[1].value;
  const isCustomType = displayType === FOCUS_DISPLAY_TYPE[3].value;
  const isBannerStyle = extras.style === FOCUS_ITEM_STYLE.banner;
  const isHorizontalStyle = extras.style === FOCUS_ITEM_STYLE.horizontal;

  const numRows = isGridType ? (mobile ? 2 : 4) : isListType ? (mobile ? 1 : 2) : (mobile ? extras.rows : extras.rows_web) ?? 1;
  const totalItems = extras.total_items ?? numRows;
  const aspectRatio = isGridType ? 166 / 250 : isListType ? 720 / 362 : (mobile ? extras.ar : extras.ar_web);
  const heightFactor = (mobile ? extras.hf : extras.hf_web);


  const type = finalData?.type;
  const _showTitle = showTitle && !isProductViewType && extras.show_title;

  const findCard = useCallback(
      (dragId) => {
        if (!allowDrag) return
        const card = finalData.items?.find((c) => c.id === dragId)
        return {
          card,
          index: card ? finalData.items?.indexOf(card) : -1,
        }
      },
      [finalData, allowDrag],
  )
  
  const moveCard = useCallback(
      (dragId, atIndex) => {
        if (processing || !allowDrag) return
        const { card, index } = findCard(dragId)
        if (!card || index === -1) return
        const tempArray = [...finalData.items]
        tempArray.splice(index, 1)
        tempArray.splice(atIndex, 0, card)
        setModifiedData({ ...finalData, items: tempArray })
      },
      [findCard, finalData, processing, allowDrag],
  )
  
  const handleDrop = useCallback(() => {
      onUpdateFocusItem?.(finalData, true)
  }, [finalData, onUpdateFocusItem])

  return (
    <div className={`w-full${showTitle ? ' app-top-mg' : ''} ${mobile ? 'mobile' : 'web'}`}>
      {
        _showTitle &&
        <ViewAllTitle title={finalData?.title} isLoading={loading} mobile={mobile} hasViewAllBtn={false}
                      titleMargin={`${isHorizontalStyle ? "mx-24" : "mx-10"} mt-8`}/>
      }
      {(isProductViewType && !error) &&
        <MemoizedFocusedItemsProductView type={type} mobile={mobile} focused={{data: finalData, init, error}}
                                         showTitle={extras.show_title}/>}


      {(!isProductViewType || !!error) &&
        <div style={{gridTemplateColumns: `repeat(${numRows}, minmax(0, 1fr))`}}
             className={`w-full${isBannerStyle || error ? '' : isHorizontalStyle ? '' : ` grid gap-4 ${sideClass}`}`}>
          {
            error ? <FocusedError errorMsg={error} retry={fetchFocusedItems}/>
              : loading ?
                <FocusedLoader isBannerStyle={isBannerStyle} hf={heightFactor} mobile={mobile} extras={extras}
                               length={totalItems}
                               isGridType={isGridType} ar={aspectRatio} isHorizontalStyle={isHorizontalStyle}
                               hasTitleSection={isGridType || isListType || extras.use_image_titles}/>
                : init && finalData?.items?.length ?

                  <>
                    {isBannerStyle ?
                      <CustomCarousel bannerList={{data: {page_info: {extras}, mobile: finalData.items}, init: true}}
                                      mobile={mobile} showArrowButton={true}/> :
                      isHorizontalStyle ?
                        <FocusedHorizontalList mobile={mobile} data={finalData.items} arrow={extras?.arrow} hasTitles={extras.use_image_titles}
                                               heightFactor={heightFactor} aspectRatio={aspectRatio}
                                               findCard={findCard} moveCard={moveCard} allowDrag={allowDrag}
                                               onUpdateFocusItem={onUpdateFocusItem} handleDrop={handleDrop}
                                               processing={processing}/> :
                        finalData.items.map((i) =>
                          allowDrag ? (
                                              <DraggableFocusItemCard
                                                key={i.id}
                                                dragId={i.id}
                                                findCard={findCard}
                                                moveCard={moveCard}
                                                showDragHandle={finalData.items?.length > 1}
                                                onDrop={
                                                  allowDrag && onUpdateFocusItem ? handleDrop : undefined
                                                }
                                                dragHandleOnly={false}
                                                dragDisabled={!allowDrag || processing}
                                              >
                                                <FocusItemCard
                                                  key={i.id}
                                                  i={i}
                                                  mobile={mobile}
                                                  isCustomType={isCustomType}
                                                  isGridType={isGridType}
                                                  aspectRatio={aspectRatio}
                                                  extras={extras}
                                                />
                                              </DraggableFocusItemCard>
                                            ) : (
                                              <FocusItemCard
                                                key={i.id}
                                                i={i}
                                                mobile={mobile}
                                                isCustomType={isCustomType}
                                                isGridType={isGridType}
                                                aspectRatio={aspectRatio}
                                                extras={extras}
                                              />
                                            )
                         
                        )
                    }
                  </>
                  : <FocusedNoData/>
          }
        </div>}
    </div>
  );
};

export const MemoizedFocusedItemsContents = React.memo(FocusedItemsContents);



